import { Check, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger, usePopoverContext } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  useCommandContext,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

export interface ComboboxOption {
  value: string
  label: string
}

interface FilteredItemsProps {
  options: ComboboxOption[]
  value: string | null
  onSelect: (value: string) => void
  emptyMessage: string
}

function FilteredItems({ options, value, onSelect, emptyMessage }: FilteredItemsProps) {
  const { query } = useCommandContext()
  const { setOpen } = usePopoverContext()

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options

  if (!filtered.length) {
    return <CommandEmpty>{emptyMessage}</CommandEmpty>
  }

  return (
    <>
      {filtered.map((option) => (
        <CommandItem
          key={option.value}
          onClick={() => {
            onSelect(option.value)
            setOpen(false)
          }}
        >
          <Check
            className={cn(
              'mr-2 h-4 w-4 shrink-0',
              value === option.value ? 'opacity-100' : 'opacity-0'
            )}
          />
          {option.label}
        </CommandItem>
      ))}
    </>
  )
}

interface ComboboxProps {
  options: ComboboxOption[]
  value: string | null
  onChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  contentClassName?: string
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'Sin resultados.',
  disabled = false,
  className,
  contentClassName,
}: ComboboxProps) {
  const label = value ? (options.find((o) => o.value === value)?.label ?? placeholder) : placeholder

  return (
    <Popover className="w-full">
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-10 w-full justify-between border-border bg-background font-normal',
            className
          )}
        >
          <span className={cn('min-w-0 truncate', !value && 'text-muted-foreground')}>{label}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn('p-0', contentClassName ?? 'w-64')}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandGroup>
              <FilteredItems
                options={options}
                value={value}
                onSelect={onChange}
                emptyMessage={emptyMessage}
              />
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
